import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { HardwareFieldDefinition } from '../../../services/typesService';
import { getTypeBadgeColor } from '../../../utils/fieldTypes';
import { parseOptions } from '../../configTemplates/templateSchema';
import { CreateDeviceState } from './useCreateDevice';

interface RowProps {
  def: HardwareFieldDefinition;
  value: string;
  error?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

/** One schema field: its name, its type and the control that fits the type. */
const SchemaFieldRow: React.FC<RowProps> = ({ def, value, error, disabled, onChange }) => {
  const { t } = useI18n();
  // The default is shown as placeholder: what the gateway gets if the field stays blank.
  const placeholder = def.default_value ? t('devices.create.defaultPlaceholder', { value: String(def.default_value) }) : def.placeholder || '—';

  const control = (() => {
    switch (def.value_type) {
      case 'boolean':
        return (
          <label className="cell-checkbox is-left">
            <input type="checkbox" disabled={disabled} checked={value === 'true'} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />
            <span className="cell-bool-text">{value === 'true' ? t('common.yes') : t('common.no')}</span>
          </label>
        );
      case 'password':
        return <input type="password" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
      case 'port':
      case 'number':
        return <input type="text" aria-label={def.field_name} inputMode="numeric" className="cell-input mono" value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ''))} />;
      case 'dropdown':
        return (
          <select aria-label={def.field_name} className="cell-select" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
            <option value="">{placeholder}</option>
            {parseOptions(def.options).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      default:
        return <input type="text" aria-label={def.field_name} className="cell-input mono" value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
    }
  })();

  return (
    <tr className={error ? 'row-invalid' : ''}>
      <td><code>{def.field_name}</code></td>
      <td><span className="type-badge" style={{ backgroundColor: getTypeBadgeColor(def.value_type) }}>{def.value_type}</span></td>
      <td>
        {control}
        {error && <div className="field-error is-small">{error}</div>}
      </td>
    </tr>
  );
};

/** The fields the gateway type defines, grouped as in the schema, with a "fill defaults" shortcut. */
export const DeviceFieldsSection: React.FC<{ state: CreateDeviceState }> = ({ state }) => {
  const { t } = useI18n();
  const { form, schema, groupedSchema, errors, submitting } = state;

  return (
    <div className="device-section connection-section">
      <div className="section-title-row">
        <h3 className="section-title">{t('devices.configurationFields')}</h3>
        {state.canFillDefaults && (
          <button type="button" className="btn btn-secondary btn-sm" data-testid="btn-fill-defaults" onClick={state.fillDefaults} disabled={submitting} title={t('devices.fillDefaultsTitle')}>
            {t('devices.fillDefaults')}
          </button>
        )}
      </div>

      {schema && schema.length === 0 ? (
        <p className="template-hint is-padded">
          {t('devices.noFieldDefsConfigured')} <strong>{form.deviceType}</strong>.{t('devices.fieldDefsHint')}
        </p>
      ) : (
        <div className="field-defs-list">
          {groupedSchema.map((group) => (
            <div className="field-group" key={group.name}>
              <h4 className="group-header">{group.name}</h4>
              <table className="field-defs-table">
                <thead>
                  <tr>
                    <th>{t('devices.fieldName')}</th>
                    <th>{t('devices.typeLabel')}</th>
                    <th>{t('devices.value')}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.fields.map((def) => (
                    <SchemaFieldRow key={def.id} def={def} value={state.fieldValue(def)} error={errors[def.field_name]} disabled={submitting} onChange={(value) => state.setFieldValue(def, value)} />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
